import { useEffect, useState } from 'react'
import { Dialog } from './Dialog'
import { useHubLang } from '../lib/i18n'
import { PHOTO_WHAT } from '../lib/hubCopy'
import { FACETS, loadTagGuide, loadTagNotes, setTagNote, type TagGuideRow } from '../lib/photoRoom'

/**
 * 302 (FED-70). The Gallery admin's tag list: what One is told about each tag.
 *
 * Nothing trains the model. Each time One reads a photo it is given every
 * tag with the note written here, how often people said yes or no when it
 * suggested that tag, and - brands only - up to two photos a person
 * confirmed. So the note is the lever a Gallery admin has: "Lankelma: yellow
 * rigs and vehicles with green lettering" (David, 20 Oct) is a sentence, not
 * a training run. The counts are photos, never people (rule 8).
 */
export function TagListTray({ tenantId, onClose }: { tenantId: string; onClose: () => void }) {
  const t = PHOTO_WHAT[useHubLang()]
  const [rows, setRows] = useState<TagGuideRow[] | null>(null)
  /* 305: per tag, the colleagues' reasons One is told — shown exactly as One reads them. */
  const [notes, setNotes] = useState<Map<string, string[]>>(new Map())
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    loadTagGuide(tenantId)
      .then((r) => live && setRows(r))
      .catch(() => live && setError(t.couldNotLoad))
    loadTagNotes(tenantId)
      .then((n) => live && setNotes(n))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [tenantId, t.couldNotLoad])

  return (
    <Dialog open variant="tray" title={t.listTitle} onClose={onClose} className="taglist">
      <p className="taglist__intro">{t.listIntro}</p>
      {error && (
        <p className="taglist__notice" role="status">
          {error}
        </p>
      )}
      {rows &&
        FACETS.map((facet) => {
          const here = rows.filter((r) => r.facet === facet)
          if (here.length === 0) return null
          return (
            <section key={facet} className="taglist__facet">
              <h3 className="taglist__fh">{t.facet[facet]}</h3>
              <ul className="taglist__rows">
                {here.map((r) => (
                  <TagRow key={r.valueId} row={r} notes={notes.get(r.valueId) ?? []} />
                ))}
              </ul>
            </section>
          )
        })}
    </Dialog>
  )
}

function TagRow({ row, notes }: { row: TagGuideRow; notes: string[] }) {
  const t = PHOTO_WHAT[useHubLang()]
  const [note, setNote] = useState(row.note ?? '')
  const [saved, setSaved] = useState(row.note ?? '')
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle')
  const id = `tagnote-${row.valueId}`

  async function save() {
    setState('saving')
    try {
      await setTagNote(row.valueId, note)
      setSaved(note.trim())
      setNote(note.trim())
      setState('saved')
    } catch {
      setState('failed')
    }
  }

  return (
    <li className="taglist__row">
      <div className="taglist__name">{row.labelEn}</div>
      <div className="taglist__record">
        {t.record(row.oneSuggested, row.peopleConfirmed, row.peopleRejected, row.peopleAdded)}
        {row.facet === 'brand' && <> · {t.references(row.references)}</>}
      </div>
      {/* 305: what colleagues said — the last three that agreed with where a tag ended. */}
      <div className="taglist__notes">
        <span className="taglist__label">{t.notesLabel}</span>
        {notes.length ? (
          <ul className="taglist__notelist">
            {notes.map((n, i) => (
              <li key={i}>“{n}”</li>
            ))}
          </ul>
        ) : (
          <span className="taglist__status">{t.notesNone}</span>
        )}
      </div>
      <label className="taglist__label" htmlFor={id}>
        {t.noteLabel}
      </label>
      <div className="taglist__edit">
        <textarea
          id={id}
          rows={2}
          maxLength={280}
          value={note}
          placeholder={t.notePlaceholder}
          onChange={(e) => {
            setNote(e.target.value)
            setState('idle')
          }}
        />
        <button
          type="button"
          className="btn btn--quiet btn--sm"
          disabled={state === 'saving' || note.trim() === saved}
          onClick={() => void save()}
        >
          {t.save}
        </button>
      </div>
      {state === 'saved' && (
        <p className="taglist__status" role="status">
          {t.saved}
        </p>
      )}
      {state === 'failed' && (
        <p className="taglist__status taglist__status--warn" role="status">
          {t.couldNotSave}
        </p>
      )}
    </li>
  )
}
